import { faker } from "@faker-js/faker";
import { sample } from "lodash";
import {
  Institution,
  ProgramMembership,
  InvitationStatus,
  Program,
  ProgramRole,
  User,
  UserInvitation,
  TraineeType
} from "../API";

export const generateProgram = ({
  institution = generateInstitution()
} = {}): Program => {
  const createdAt = faker.date.past();
  const newProgram: Program = {
    __typename: "Program",
    id: faker.datatype.uuid(),
    name: faker.commerce.department(),
    description: faker.lorem.sentence(),
    institution: institution,
    institutionId: institution.id,
    createdAt: createdAt.toISOString(),
    updatedAt: faker.date.between(createdAt, new Date()).toISOString()
  };
  return newProgram;
};

export const generatePrograms = ({
  numToGenerate = 1,
  institution = generateInstitution()
} = {}): Program[] => {
  const generatedPrograms: Program[] = [];
  while (numToGenerate > 0) {
    const newProgram = generateProgram({ institution });
    generatedPrograms.push(newProgram);
    numToGenerate--;
  }
  return generatedPrograms;
};

export const generateMembership = (
  userId: string,
  programId: string,
  isProgramAdmin: boolean
): ProgramMembership => {
  const createdAt = faker.date.past();
  const generatedMembership: ProgramMembership = {
    __typename: "ProgramMembership",
    id: faker.datatype.uuid(),
    programRole: isProgramAdmin
      ? ProgramRole.ProgramAdmin
      : ProgramRole.Trainee,
    programMemberId: userId,
    programId: programId,
    createdAt: createdAt.toISOString(),
    updatedAt: faker.date.between(createdAt, new Date()).toISOString()
  };
  return generatedMembership;
};

export const generateMemberships = (
  numToGenerate: number,
  userId: string,
  institution: Institution,
  isProgramAdmin = false
): ProgramMembership[] => {
  const generatedMemberships: ProgramMembership[] = [];
  while (numToGenerate > 0) {
    const program = generateProgram({ institution });
    const newMembership = generateMembership(
      userId,
      program.id,
      isProgramAdmin
    );
    generatedMemberships.push(newMembership);
    numToGenerate--;
  }
  return generatedMemberships;
};

export const generateMembershipsForPrograms = (
  userId: string,
  programs: Program[],
  isProgramAdmin: boolean
): ProgramMembership[] =>
  programs.map((p) => generateMembership(userId, p.id, isProgramAdmin));

interface GenerateUserProps {
  hasLoggedIn?: boolean;
  hasNPI?: boolean;
  existingMemberships?: number;
  institution?: Institution;
  noInstitutionId?: boolean;
  isProgramAdmin?: boolean;
  programs?: Program[];
}
export const generateUser = ({
  hasLoggedIn = false,
  hasNPI = false,
  existingMemberships = 0,
  institution = generateInstitution(),
  noInstitutionId = false,
  isProgramAdmin = false,
  programs = undefined
}: GenerateUserProps = {}): User => {
  const userId = faker.datatype.uuid();
  const createdAt = faker.date.past(3);
  const updatedAt = faker.date.between(createdAt, new Date());

  let memberships;
  if (programs) {
    memberships = generateMembershipsForPrograms(
      userId,
      programs,
      isProgramAdmin
    );
  } else {
    memberships = generateMemberships(
      existingMemberships,
      userId,
      institution,
      isProgramAdmin
    );
  }

  const generatedUser: User = {
    __typename: "User",
    id: userId,
    createdAt: createdAt.toISOString(),
    email: faker.internet.email(),
    firstName: faker.name.firstName(),
    lastLogin: hasLoggedIn ? faker.date.recent().toISOString() : null,
    lastName: faker.name.lastName(),
    npi: hasNPI
      ? faker.datatype.number({ min: 1000000000, max: 1999999999 })
      : null,
    pgy: faker.datatype.number({ min: 1, max: 5 }),
    updatedAt: updatedAt.toISOString(),
    institutionId: !noInstitutionId ? institution.id : "",
    institution: institution,
    programMemberships: {
      __typename: "ModelProgramMembershipConnection",
      items: memberships
    }
  };
  return generatedUser;
};

interface GenerateProgramMembershipProps {
  user?: User;
  program?: Program;
  institution?: Institution;
  programRole?: ProgramRole;
}
export const generateProgramMembership = ({
  user = generateUser(),
  program = generateProgram(),
  programRole = ProgramRole.Trainee
}: GenerateProgramMembershipProps): ProgramMembership => {
  const createdAt = faker.date.past(3);
  const updatedAt = faker.date.between(createdAt, new Date());
  return {
    __typename: "ProgramMembership",
    id: faker.datatype.uuid(),
    programRole: programRole,
    programMemberId: user.id,
    programId: program.id,
    createdAt: createdAt.toISOString(),
    updatedAt: updatedAt.toISOString(),
    traineeType: TraineeType.Categorical
  };
};

export const generateUsers = ({
  numToGenerate = 0,
  institution = generateInstitution()
} = {}): User[] => {
  const generatedUsers = [];
  while (numToGenerate > 0) {
    const newUser = generateUser({ institution });
    generatedUsers.push(newUser);
    numToGenerate--;
  }
  return generatedUsers;
};

export const generateInstitution = (): Institution => {
  const createdAt = faker.date.past();
  const newInstitution: Institution = {
    __typename: "Institution",
    id: faker.datatype.uuid(),
    name: faker.company.name(),
    description: faker.lorem.sentence(),
    createdAt: createdAt.toISOString(),
    updatedAt: faker.date.between(createdAt, new Date()).toISOString()
  };
  return newInstitution;
};

export const generateInstitutions = (numToGenerate: number): Institution[] => {
  const generatedInstitutions: Institution[] = [];
  while (numToGenerate > 0) {
    const newInstitution = generateInstitution();
    generatedInstitutions.push(newInstitution);
    numToGenerate--;
  }
  return generatedInstitutions;
};

export const generateUserInvitation = ({
  institution = generateInstitution(),
  invitee = generateUser({ institution }),
  program = generateProgram({ institution }),
  role = sample([
    ProgramRole.Trainee,
    ProgramRole.Attending,
    ProgramRole.ProgramAdmin,
    ProgramRole.Admin
  ]),
  resolved = false
} = {}): UserInvitation => {
  const createdAt = faker.date.past();
  const newInvitation: UserInvitation = {
    __typename: "UserInvitation",
    id: faker.datatype.uuid(),
    invitee: invitee,
    institutionId: institution.id,
    invitationStatus: resolved
      ? InvitationStatus.Resolved
      : InvitationStatus.Started,
    email: invitee.email,
    inviteeId: invitee.id,
    programId: program.id,
    createdAt: createdAt.toISOString(),
    updatedAt: faker.date.between(createdAt, new Date()).toISOString(),
    role: role
  };
  return newInvitation;
};

export const generateUserInvitations = ({
  numToGenerate = 5,
  institution = generateInstitution()
}): UserInvitation[] => {
  const generatedUserInvitations: UserInvitation[] = [];
  while (numToGenerate > 0) {
    const newUserInvitation = generateUserInvitation({
      institution: institution
    });
    generatedUserInvitations.push(newUserInvitation);
    numToGenerate--;
  }
  return generatedUserInvitations;
};

export const generateUUID = () => {
  const uuid = faker.datatype.uuid();
  return uuid;
};
